import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import api from '../services/api';

const fechaLocal = (fecha) =>
  `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`;

export default function ModalReportesCartera({ onClose }) {
  const [desde, setDesde] = useState(() => {
    const hoy = new Date();
    return fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  });
  const [hasta, setHasta] = useState(() => fechaLocal(new Date()));
  const [generando, setGenerando] = useState('');
  const [error, setError] = useState('');
  const [tipoAuditoria, setTipoAuditoria] = useState('C');
  const [terceroAuditoria, setTerceroAuditoria] = useState('');
  const [tercerosAuditoria, setTercerosAuditoria] = useState([]);
  const [cargandoTerceros, setCargandoTerceros] = useState(true);
  const [errorTerceros, setErrorTerceros] = useState('');

  useEffect(() => {
    let vigente = true;
    setCargandoTerceros(true);
    setErrorTerceros('');
    const params = tipoAuditoria === 'C' ? { tipo: 'cliente', solo_principales: 1 } : { tipo: 'proveedor' };
    api.get('/api/terceros', { params }).then(({ data }) => {
      if (vigente) setTercerosAuditoria(Array.isArray(data) ? data : []);
    }).catch(() => {
      if (vigente) setErrorTerceros('No se pudo cargar la lista. Cambia el tipo para intentar nuevamente.');
    }).finally(() => {
      if (vigente) setCargandoTerceros(false);
    });
    return () => { vigente = false; };
  }, [tipoAuditoria]);

  const descargar = async (tipoMovimiento) => {
    if (!desde || !hasta) return setError('Ingresa las fechas desde y hasta.');
    if (desde > hasta) return setError('La fecha desde no puede ser posterior a la fecha hasta.');
    if (tipoMovimiento === 'PO' && tipoAuditoria !== 'C') return setError('Selecciona Clientes y el cliente para el reporte de PO.');
    if (['AUDITORIA','PO'].includes(tipoMovimiento) && (!terceroAuditoria || cargandoTerceros ||
        !tercerosAuditoria.some(t => String(t.idtercero) === terceroAuditoria))) {
      return setError('Selecciona el cliente o proveedor para la auditoría.');
    }
    setError('');
    setGenerando(tipoMovimiento);
    try {
      const auditoria = tipoMovimiento === 'AUDITORIA';
      const po = tipoMovimiento === 'PO';
      const response = await api.get(po ? '/api/cartera/reporte-po/excel' : auditoria ? '/api/cartera/auditoria-pagos' : '/api/cartera/pendientes-mensual/excel', {
        params: po ? { desde, hasta, idtercero: Number(terceroAuditoria) } : auditoria ? { desde, hasta, tipoMovimiento: tipoAuditoria, idtercero: Number(terceroAuditoria) } : { tipoMovimiento, desde, hasta },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const lado = tipoMovimiento === 'P' ? 'PROVEEDORES' : 'CLIENTES';
      link.href = url;
      link.download = po ? `FACTURAS_PO_${desde}_${hasta}.xlsx` : auditoria ? `AUDITORIA_PAGOS_${desde}_${hasta}.xlsx` : `PENDIENTES_${lado}_${desde}_${hasta}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('No se pudo descargar el reporte. Intenta nuevamente.');
    } finally {
      setGenerando('');
    }
  };

  return (
    <Dialog open onClose={generando ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="reportes-cartera-titulo">
      <DialogTitle id="reportes-cartera-titulo">Reportes de cartera</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 3 }} color="text.secondary">
          Saldos pendientes de todos los clientes o proveedores, agrupados por mes.
          Selecciona el rango de fechas para descargar el Excel.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField label="Desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth disabled={Boolean(generando)} />
          <TextField label="Hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth disabled={Boolean(generando)} />
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button variant="contained" onClick={() => descargar('C')} disabled={Boolean(generando)}>
            {generando === 'C' ? 'Generando…' : 'Clientes pendientes'}
          </Button>
          <Button variant="outlined" onClick={() => descargar('P')} disabled={Boolean(generando)}>
            {generando === 'P' ? 'Generando…' : 'Proveedores pendientes'}
          </Button>
        </Stack>
        <Typography sx={{ mt: 3, mb: 1 }} fontWeight={600}>Auditoría de pagos y cobros</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Revisa pagos de clientes y proveedores por fecha de pago, sus documentos,
          diferencias y referencias inexistentes. Selecciona a quién deseas auditar.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Auditar" value={tipoAuditoria} SelectProps={{ native: true }}
            disabled={Boolean(generando)} fullWidth onChange={(e) => {
              if (e.target.value === tipoAuditoria) return;
              setTipoAuditoria(e.target.value);
              setTerceroAuditoria('');
              setTercerosAuditoria([]);
              setError('');
            }}>
            <option value="C">Clientes</option>
            <option value="P">Proveedores</option>
          </TextField>
          <TextField select label={tipoAuditoria === 'C' ? 'Cliente' : 'Proveedor'}
            value={terceroAuditoria} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
            onChange={(e) => setTerceroAuditoria(e.target.value)} fullWidth
            disabled={Boolean(generando) || cargandoTerceros}>
            <option value="">{cargandoTerceros ? 'Cargando…' : 'Selecciona…'}</option>
            {tercerosAuditoria.map(t => <option key={t.idtercero} value={t.idtercero}>{t.nombre}</option>)}
          </TextField>
        </Stack>
        {errorTerceros && <Alert severity="error" sx={{ mb: 2 }}>{errorTerceros}</Alert>}
        <Button variant="outlined" onClick={() => descargar('AUDITORIA')}
          disabled={Boolean(generando) || cargandoTerceros || !terceroAuditoria}>
          {generando === 'AUDITORIA' ? 'Generando auditoría…' : 'Descargar auditoría de pagos'}
        </Button>
        <Typography sx={{ mt: 3, mb: 1 }} fontWeight={600}>Facturas desglosadas por PO</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Usa el cliente seleccionado arriba y el rango de fechas de factura. Incluye número,
          fecha, PO / Mark, valores y total de cada factura.
        </Typography>
        <Button variant="outlined" onClick={() => descargar('PO')}
          disabled={Boolean(generando) || cargandoTerceros || tipoAuditoria !== 'C' || !terceroAuditoria}>
          {generando === 'PO' ? 'Generando desglose…' : 'Descargar facturas por PO'}
        </Button>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={Boolean(generando)}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}
